<template>
  <v-expansion-panel>
    <v-expansion-panel-header class="text-systock-blue font-weight-medium font-14" >
      <p class="ma-0 mx-n4">Configure o Ajuste</p>
    </v-expansion-panel-header>

    <v-expansion-panel-content class="mx-n4">
      <v-radio-group v-model="props.parametros.tipo_ajuste">
        <ContainerGray
          v-for="item in tipos_ajustes"
          :inativo="props.parametros.tipo_ajuste !== item.value"
          :key="item.value"
        >
          <v-radio :label="item.text" :value="item.value" />
        </ContainerGray>
      </v-radio-group>

      <div class="text-systock-blue font-weight-medium mb-5">
        Valor Atual

        <ContainerGray class="px-2">
          <span>{{ Helpers.formatarValorMonetario(valor_atual_computed) }}</span>
        </ContainerGray>
      </div>

      <div>
        <span class="text-systock-blue font-weight-medium">Valor Desejado</span>

        <div class="d-flex align-start flex-nowrap ga-3">
          <div class="flex-1-1-0">
            <TextFieldFormatadorDeValor
              v-model="props.parametros.valor_desejado"
              outlined
              class="bra-12"
              :class="{
                'cor-verde-valor-desejado-positivo': valor_desejado_maior_que_base,
                'cor-verde-valor-desejado-negativo': !valor_desejado_maior_que_base
              }"
              :append-icon="valor_desejado_maior_que_base ? 'mdi-arrow-up' : 'mdi-arrow-down'"
              :prefixo="props.parametros.tipo_ajuste === 'POR_UNIDADE_MEDIDA' ? '' : 'R$'"
              :error="!!erro_valor_desejado"
              :error-messages="[]"
              hide-details
              @input="validarValorDesejado"
              @blur="validarValorDesejado"
              @focus="ev => ev.target.select()"
            />

            <span v-if="erro_valor_desejado" class="error-span">
              {{ erro_valor_desejado }}
            </span>
          </div>


          <v-autocomplete
            v-model="props.parametros.unidade_medida"
            v-show="props.parametros.tipo_ajuste === 'POR_UNIDADE_MEDIDA'"
            :items="unidades_medida_computed"
            label="Un. Medida"
            outlined
            class="bra-12 pl-3 flex-1-1-0"
            return-object
            item-text="descricao"
            item-value="id"
            hide-details="auto"
          />
        </div>

        <div v-if="props.parametros.tipo_ajuste === 'POR_UNIDADE_MEDIDA'">
          <ContainerGray class="px-5 pt-2 mt-5" style="background-color: #FFF2D7">
            <p class="text-systock-blue font-weight-medium ma-0 mb-1">Remoção de Produtos</p>
            <span class="font-12">Remover produtos onde a Un. Medida for diferente da selecionada e não tiver fator de conversão informado</span>

            <v-radio-group v-model="props.parametros.remover_produtos_com_un_medida_diferente" row hide-details class="my-2">
              <v-radio
                v-for="item in sim_ou_nao"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              />
            </v-radio-group>
          </ContainerGray>

          <v-divider class="my-5" />
        </div>
      </div>

      <ContainerGray class="px-5 pt-2 mt-5">
        <div class="d-flex align-center justify-space-between">
          <p class="text-systock-blue font-weight-medium ma-0 mb-1">Alterar mix de produtos</p>

          <Filtros
            v-model="props.parametros.filtros_gerais"
            :size="3"
            eager
          />
        </div>

        <v-radio-group v-model="props.parametros.alterar_mix" row hide-details class="my-2">
          <v-radio
            v-for="item in sim_ou_nao"
            :key="item.value"
            :label="item.text"
            :value="item.value"
          />
        </v-radio-group>

        <span class="font-12">{{ props.parametros.alterar_mix ? 'O mix será alterado. No aumento, trata o valor informado como mínimo. Na redução, trata o valor informado como alvo. ' : 'Ajusta o valor mantendo o mix do carrinho inalterado' }}</span>
      </ContainerGray>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script setup>
import ContainerGray from "@/components/compra/ProdutosGerais/Carrinho/Meta/ContainerGray.vue";
import TextFieldFormatadorDeValor from "@/components/utils/TextFieldFormatadorDeValor.vue";
import {computed, onMounted, ref} from "vue";
import api from "@/api/api";
import {logout} from "@/helpers";
import Filtros from "@/components/compra/ProdutosGerais/Carrinho/Meta/Ajustar/Filtros.vue";

const tipos_ajustes = [
  { text: 'Por Valor', value: 'POR_VALOR' },
  { text: 'Por Unidade de Medida', value: 'POR_UNIDADE_MEDIDA' },
];

const sim_ou_nao = [
  { text: 'Não', value: false },
  { text: 'Sim', value: true },
];

const props = defineProps({
  parametros: Object,
  produtos: Array,
  fornecedores: {
    type: Array,
    default: () => [],
  },
  valores_resumo_detalhado: Object
});

const embalagens_cadastro_produtos = ref([]);

const unidades_medida_computed = computed(() => {
  const embalagens = [...new Set(props.produtos.map(item => item.idunidade_medida))].map(item => ({id: item, descricao: item}));

  embalagens.unshift(...embalagens_cadastro_produtos.value);

  return embalagens;
});

const valor_atual_computed = computed(() => {
  return props.produtos.reduce((a, b) => a + (Number(b.qtd_inicial || 0) * Number(b.preco_custo || 0)), 0);
});

const base_comparacao_computed = computed(() => {
  if (props.parametros.tipo_ajuste === 'POR_UNIDADE_MEDIDA') {
    return Number(props.valores_resumo_detalhado.volume_ajustado);
  }

  return valor_atual_computed.value;
});

const valor_desejado_maior_que_base = computed(() => {
  return Number(props.parametros.valor_desejado) >= base_comparacao_computed.value;
});

onMounted(() => buscarEmbalagensCadastroProdutos());

function buscarEmbalagensCadastroProdutos() {
  api.get('/compras/produtos/carrinho/ajuste-pedido/embalagens-cadastro-produtos', { params: { fornecedores: props.fornecedores }})
      .then(resp => {
        embalagens_cadastro_produtos.value = resp.data.embalagens.map(item => ({
          id: item.id,
          descricao: item.descricao,
        }));
      })
      .catch(logout)
}

function regraValorMaiorQueZeroSemSomenteZeros(v) {
  const str = (v === null || v === undefined) ? '' : String(v);

  const digits = str.replace(/\D/g, '');

  if (digits === '' || !/[1-9]/.test(digits)) {
    return 'O valor desejado deverá ser um número maior que 0.';
  }

  return true;
}

const erro_valor_desejado = ref('');
function validarValorDesejado() {
  const res = regraValorMaiorQueZeroSemSomenteZeros(props.parametros.valor_desejado);
  erro_valor_desejado.value = (res === true) ? '' : String(res);
}

</script>

<style scoped>
.flex-1-1-0 { flex: 1 1 0; min-width: 0; }

.error-span {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.2;
  color: #B00020;
}

.cor-verde-valor-desejado-positivo :deep(.v-input__append-inner .v-icon) {
  color: green !important;
}

.cor-verde-valor-desejado-negativo :deep(.v-input__append-inner .v-icon) {
  color: #B00020 !important;
}
</style>

